import React, { useEffect, useState } from 'react';
import { eligibilityRulesAPI, eligibilityRulesTicgnAPI } from '../lib/api';
import { Search, Plus, Pencil, Trash2, Download, Upload } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';

const AdminCodesNAF = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'energy_manager';
  const [codes, setCodes] = useState([]);
  const [ticgnMap, setTicgnMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    naf_code_label: '',
    libelle: '',
    segment: '',
    eligibility_type: 'NON_CONCERNE',
    eligibility_reason: ''
  });

  const ELIGIBILITE_POTENTIELLE = [
    { value: 'EXONERATION', label: 'Exonération totale', color: 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' },
    { value: 'TARIF_REDUIT', label: 'Tarif réduit', color: 'bg-[#397DFF]/10 text-[#397DFF] border-[#397DFF]/20' },
    { value: 'NON_CONCERNE', label: 'Non concerné', color: 'bg-[#687175]/10 text-[#687175] border-[#687175]/20' }
  ];

  const MOTIFS_ELIGIBILITE = [
    'Activité industrielle électro-intensive',
    'Secteur exposé à un risque de fuite de carbone',
    'Procédé électro-intensif spécifique',
    'Minéralogie',
    'Chimie',
    'Production électricité/gaz',
    'Autre'
  ];

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async (searchTerm = '') => {
    try {
      const [ticfeRes, ticgnRes] = await Promise.all([
        eligibilityRulesAPI.list(searchTerm),
        eligibilityRulesTicgnAPI.list(searchTerm)
      ]);
      setCodes(ticfeRes.data || []);
      const map = {};
      (ticgnRes.data || []).forEach(r => { map[r.naf_code_label] = r; });
      setTicgnMap(map);
    } catch (error) {
      alert('Erreur chargement codes NAF');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCodes(search);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await eligibilityRulesAPI.update(editingId, formData);
        alert('Code NAF modifié');
      } else {
        await eligibilityRulesAPI.create(formData);
        alert('Code NAF créé');
      }
      setShowModal(false);
      resetForm();
      fetchCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code NAF ?')) return;
    try {
      await eligibilityRulesAPI.delete(id);
      alert('Code NAF supprimé');
      fetchCodes();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const openEditModal = (code) => {
    setEditingId(code.id);
    setFormData({
      naf_code_label: code.naf_code_label,
      libelle: code.libelle,
      segment: code.segment || '',
      eligibility_type: code.eligibility_type,
      eligibility_reason: code.eligibility_reason
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ naf_code_label: '', libelle: '', segment: '', eligibility_type: 'NON_CONCERNE', eligibility_reason: '' });
    setEditingId(null);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#397DFF]"></div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-semibold text-[#191D1E] tracking-tight">Codes NAF</h1>
          <p className="text-[#687175] mt-2">Référentiel codes NAF/APE avec motifs d'éligibilité</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowModal(true); resetForm(); }} className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-3 px-6 rounded-lg transition-colors">
            <Plus className="w-5 h-5" /> Nouveau code NAF
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg p-6 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#687175]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par code NAF ou libellé..."
              className="w-full pl-12 pr-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
            />
          </div>
          <button type="submit" className="px-6 py-3 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium rounded-lg">
            Rechercher
          </button>
        </form>
      </div>

      {/* Tableau */}
      <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Code NAF / Libellé</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">TICFE</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Motif TICFE</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">TICGN</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Motif TICGN</th>
                {isAdmin && <th className="px-6 py-4 text-right text-sm font-semibold text-[#191D1E]">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {codes.map((code) => {
                const ticgnRule = ticgnMap[code.naf_code_label];
                return (
                <tr key={code.id} className="hover:bg-[#F5F6F9] transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-mono text-[#191D1E] font-semibold">{code.naf_code_label}</div>
                    <div className="text-sm text-[#687175] mt-1">{code.libelle}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      code.eligibility_type === 'EXONERATION' ? 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' :
                      code.eligibility_type === 'TARIF_REDUIT' ? 'bg-[#397DFF]/10 text-[#397DFF] border-[#397DFF]/20' :
                      code.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20' :
                      'bg-[#687175]/10 text-[#687175] border-[#687175]/20'
                    }`}>
                      {code.eligibility_type === 'EXONERATION' ? 'Exonération' :
                       code.eligibility_type === 'TARIF_REDUIT' ? 'Tarif réduit' :
                       code.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'Tarif réduit (ratio)' :
                       'Non concerné'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#191D1E] text-sm">
                    {code.eligibility_reason || '-'}
                  </td>
                  <td className="px-6 py-4">
                    {ticgnRule ? (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        ticgnRule.eligibility_type === 'EXONERATION' ? 'bg-[#10D183]/10 text-[#10D183] border-[#10D183]/20' :
                        ticgnRule.eligibility_type === 'TARIF_REDUIT' ? 'bg-[#FF6B35]/10 text-[#FF6B35] border-[#FF6B35]/20' :
                        ticgnRule.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20' :
                        'bg-[#687175]/10 text-[#687175] border-[#687175]/20'
                      }`}>
                        {ticgnRule.eligibility_type === 'EXONERATION' ? 'Exonération' :
                         ticgnRule.eligibility_type === 'TARIF_REDUIT' ? 'Tarif réduit' :
                         ticgnRule.eligibility_type === 'TARIF_REDUIT_AVEC_RATIO' ? 'Tarif réduit (ratio)' :
                         'Non concerné'}
                      </span>
                    ) : (
                      <span className="text-xs text-[#BFC6CC] italic">aucune règle</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-[#191D1E] text-sm">
                    {ticgnRule ? (ticgnRule.eligibility_reason || '-') : '-'}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => openEditModal(code)} className="inline-flex items-center gap-1 text-[#397DFF] hover:text-[#4672B6] mr-3">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(code.id)} className="inline-flex items-center gap-1 text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-[#F5F6F9] border-t border-[#BFC6CC] text-sm text-[#687175]">
          {codes.length} code(s) NAF
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
              {editingId ? 'Modifier' : 'Nouveau'} Code NAF
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Code NAF *</label>
                  <input
                    type="text"
                    required
                    value={formData.naf_code_label}
                    onChange={(e) => setFormData({...formData, naf_code_label: e.target.value})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] font-mono"
                    placeholder="Ex: C1392"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Segment</label>
                  <input
                    type="text"
                    value={formData.segment}
                    onChange={(e) => setFormData({...formData, segment: e.target.value})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                    placeholder="Ex: Industrie Manufacturière"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Libellé *</label>
                <input
                  type="text"
                  required
                  value={formData.libelle}
                  onChange={(e) => setFormData({...formData, libelle: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="Description de l'activité"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Éligibilité Potentielle *</label>
                <div className="space-y-2">
                  {ELIGIBILITE_POTENTIELLE.map((elig) => (
                    <label key={elig.value} className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.eligibility_type === elig.value ? 'border-[#397DFF] bg-[#E8F4FD]' : 'border-[#BFC6CC] hover:border-[#397DFF]'}`}>
                      <input
                        type="radio"
                        name="eligibility_type"
                        value={elig.value}
                        checked={formData.eligibility_type === elig.value}
                        onChange={(e) => setFormData({...formData, eligibility_type: e.target.value})}
                      />
                      <div>
                        <div className="font-medium text-[#191D1E]">{elig.label}</div>
                        <div className="text-xs text-[#687175]">{elig.color}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Motif d'Éligibilité *</label>
                <select
                  required
                  value={formData.eligibility_reason}
                  onChange={(e) => setFormData({...formData, eligibility_reason: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                >
                  <option value="">Sélectionner un motif</option>
                  {MOTIFS_ELIGIBILITE.map((motif) => (
                    <option key={motif} value={motif}>{motif}</option>
                  ))}
                </select>
                <p className="text-xs text-[#687175] mt-1">Ou saisissez un motif personnalisé</p>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] font-medium rounded-lg hover:bg-[#F5F6F9]">
                  Annuler
                </button>
                <button type="submit" className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white font-medium py-3 px-6 rounded-lg">
                  {editingId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCodesNAF;
